import { useEffect, useMemo, useState } from "react";
import { EyeOff, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Designer } from "@/components/designer/Designer";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";
import { CATEGORY_LABELS } from "@/lib/components";
import { fromLayout, toLayout } from "@/lib/design";
import { loadProducts } from "@/components/quote/ProductPicker";

const odd = (p) => (p.flags || []).length > 0;
const CATS = Object.entries(CATEGORY_LABELS).filter(([k]) => k !== "screen");
const FIELDS = ["name", "code", "line_category", "color", "grid", "layout", "config", "hidden"];

function ProductDialog({ p, onClose, onSaved }) {
  const [f, setF] = useState(() => Object.fromEntries(FIELDS.map((k) => [k, p[k] ?? ""])));
  const [design, setDesign] = useState(false);
  const [err, setErr] = useState("");
  const slider = ["sliding_window", "sliding_door", "pocket_door"].includes(f.line_category);
  const save = async () => {
    try {
      await api.put(`/settings/catalog/products/${p.id}`, { ...f, grid: !!f.grid, hidden: !!f.hidden });
      toast.success("Product saved");
      onSaved();
      onClose();
    } catch (e) { setErr(errorMessage(e)); }
  };
  const field = (k, label, extra = {}) => (
    <label className="text-xs text-slate-400">{label}
      <input data-testid={`catalog-edit-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inputCls} ${extra.mono ? "font-mono" : ""}`} /></label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="catalog-edit-dialog" className={`max-h-[92vh] overflow-y-auto bg-slate-950 ${design ? "max-w-6xl" : "max-w-xl"}`}>
        <DialogHeader><DialogTitle className="font-display text-slate-50">Edit {p.code}</DialogTitle></DialogHeader>
        {design ? (
          <Designer initial={fromLayout(f.layout || "X", 72, 60, f.line_category)} saveLabel="Use this layout"
            onSave={(d) => { setF({ ...f, layout: toLayout(d) }); setDesign(false); }} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {field("name", "Name")}{field("code", "Code", { mono: true })}
            <label className="text-xs text-slate-400">Type / product line category
              <select data-testid="catalog-edit-line_category" value={f.line_category} onChange={(e) => setF({ ...f, line_category: e.target.value })} className={inputCls}>
                {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></label>
            {field("color", "Color")}
            {slider ? field("config", "Panels (e.g. XO, OXXO)", { mono: true }) : (
              <div className="text-xs text-slate-400">Panel layout
                <div className="mt-1 flex gap-2">
                  <input data-testid="catalog-edit-layout" value={f.layout} onChange={(e) => setF({ ...f, layout: e.target.value })} className={`${inputCls} font-mono`} />
                  <button data-testid="catalog-edit-in-designer" onClick={() => setDesign(true)} className="shrink-0 rounded-full border border-amber-500/40 px-3 text-xs text-amber-300 hover:bg-amber-500/10">Edit in designer</button>
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" data-testid="catalog-edit-grid" checked={!!f.grid} onChange={(e) => setF({ ...f, grid: e.target.checked })} /> Grids</label>
            <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" data-testid="catalog-edit-hidden" checked={!!f.hidden} onChange={(e) => setF({ ...f, hidden: e.target.checked })} /> Hidden from quotes</label>
            <p className="text-xs text-slate-500 sm:col-span-2">Photo: shared by its category and color. Regenerate or upload it in the photo grid below.</p>
          </div>
        )}
        {err && <p data-testid="catalog-edit-error" className="text-xs text-red-400">{err}</p>}
        {!design && (
          <DialogFooter>
            <button data-testid="catalog-edit-cancel" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-slate-300">Cancel</button>
            <button data-testid="catalog-edit-save" onClick={save} className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400">Save</button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Every GE78/MT150 catalog product, editable (unusual layouts first so they can be fixed in the designer).
export function CatalogProductsEditor() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [onlyOdd, setOnlyOdd] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => loadProducts(true).then(setList).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((p) => (!onlyOdd || odd(p)) && (!s || `${p.code} ${p.name}`.toLowerCase().includes(s))).slice(0, 200);
  }, [list, q, onlyOdd]);
  const oddN = list.filter(odd).length;
  return (
    <section data-testid="catalog-products-editor" className="rounded-2xl border border-slate-800 p-5">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Catalog products</h2>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input data-testid="catalog-products-search" placeholder="Search code or name" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls.replace("w-full", "")} w-72`} />
        <label className="flex items-center gap-1.5 text-xs text-slate-300"><input type="checkbox" data-testid="catalog-products-unusual" checked={onlyOdd} onChange={(e) => setOnlyOdd(e.target.checked)} /> Flagged only ({oddN})</label>
        <span className="text-xs text-slate-500">{shown.length} of {list.length}{shown.length === 200 ? " (first 200)" : ""}</span>
      </div>
      <div className="mt-3 max-h-[60vh] overflow-auto">
        <table className="w-full text-left text-xs">
          <tbody>{shown.map((p, i) => (
            <tr key={p.id} data-testid={`catalog-product-row-${i}`} className={`border-t border-slate-800/60 ${p.hidden ? "opacity-50" : ""}`}>
              <td className="py-1.5 pr-2 font-mono text-slate-300">{p.code}</td>
              <td className="pr-2 text-slate-200">{p.name}{p.hidden && <EyeOff className="ml-1 inline h-3 w-3" />}</td>
              <td className="pr-2 text-slate-400">{CATEGORY_LABELS[p.line_category]}</td>
              <td className="pr-2 font-mono text-slate-400">{p.layout || p.config}{odd(p) && <span title={p.flags.join("; ")} className="ml-1 text-amber-300">flagged</span>}</td>
              <td className="text-right"><button aria-label="Edit" data-testid={`catalog-product-edit-${i}`} onClick={() => setEdit(p)} className="p-1 text-slate-400 hover:text-amber-200"><Pencil className="h-3.5 w-3.5" /></button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {edit && <ProductDialog p={edit} onClose={() => setEdit(null)} onSaved={load} />}
    </section>
  );
}
